import {useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {useForm} from 'react-hook-form'
import {ALL_RESOURCE_TYPES, IResource, ResourceType} from '../../core/interfaces'
import {createResource, updateResource, uploadResourceFile} from '../../services/resources'

export interface ResourceModalProps {
    show: boolean
    onClose: () => void
    onSaved: () => void
    editResource: IResource | null
}

interface ResourceFormValues {
    name: string
    type: ResourceType
}

export const ResourceModal = ({show, onClose, onSaved, editResource}: ResourceModalProps): JSX.Element => {
    const [isUploading, setIsUploading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [uploadedKey, setUploadedKey] = useState<string>('')
    const [uploadedFileName, setUploadedFileName] = useState<string>('')

    const {register, handleSubmit, reset, setValue, formState: {errors}} = useForm<ResourceFormValues>({
        defaultValues: {name: '', type: 'PARENTS_GUIDE'},
    })

    useEffect(() => {
        if (show) {
            setError(null)
            if (editResource) {
                setValue('name', editResource.name)
                setValue('type', editResource.type)
                setUploadedKey(editResource.key || '')
                setUploadedFileName(editResource.key ? editResource.key.split('/').pop() || '' : '')
            } else {
                reset()
                setUploadedKey('')
                setUploadedFileName('')
            }
        }
    }, [show, editResource])

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setIsUploading(true)
        setError(null)
        try {
            const result = await uploadResourceFile(file)
            setUploadedKey(result.key)
            setUploadedFileName(file.name)
        } catch {
            setError('File upload failed. Please try again.')
        } finally {
            setIsUploading(false)
        }
    }

    const submit = async (values: ResourceFormValues) => {
        if (!uploadedKey) {
            setError('Please upload a resource file.')
            return
        }
        setIsSaving(true)
        setError(null)
        try {
            if (editResource) {
                await updateResource(editResource.id as string, {
                    name: values.name,
                    type: values.type,
                    key: uploadedKey,
                })
            } else {
                await createResource({
                    name: values.name,
                    type: values.type,
                    key: uploadedKey,
                })
            }
            onSaved()
            onClose()
        } catch {
            setError('Failed to save resource. Please try again.')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Modal show={show} onHide={onClose} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{editResource ? 'Edit Resource' : 'Create Resource'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form id="resource-form" onSubmit={handleSubmit(submit)}>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            {...register('name', {required: 'Name is required'})}
                            type="text"
                            placeholder="Resource name"
                            isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">{errors.name?.message}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Type</Form.Label>
                        <Form.Select {...register('type', {required: true})}>
                            {ALL_RESOURCE_TYPES.map(t => (
                                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>File (PDF / document)</Form.Label>
                        <Form.Control
                            type="file"
                            onChange={handleFileChange}
                            disabled={isUploading}
                        />
                        {isUploading && (
                            <div className="mt-2 d-flex align-items-center gap-2">
                                <Spinner size="sm" />
                                <small>Uploading...</small>
                            </div>
                        )}
                        {uploadedFileName && !isUploading && (
                            <small className="text-success mt-1 d-block">
                                Uploaded: {uploadedFileName}
                            </small>
                        )}
                        {!uploadedKey && !isUploading && (
                            <small className="text-muted mt-1 d-block">No file uploaded yet.</small>
                        )}
                    </Form.Group>

                    {error && <div className="alert alert-danger">{error}</div>}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="primary"
                    type="submit"
                    form="resource-form"
                    disabled={isSaving || isUploading}
                >
                    {isSaving ? <Spinner size="sm" /> : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
